import React, { Component } from "react";
import { getEmptyImage } from "react-dnd-html5-backend";
import { DragSource } from "react-dnd";
import { canvasMargin } from "views/components/Editor/canvas/canvasConstants";
import { pick, noop, isNil, some, getPath } from "lib/lodash";
import { jsonStringEqual } from "lib/equalityUtils";
import { getElementComponent } from "views/components/Editor/elements/elementsComponentsMap";
import { SnapCoordinatesSystem, SnapElement } from "lib/SnapSystems";
import { parseLinkUrl } from "lib/parseLinkUrl";
import PATHS from "routes/paths";
import EditorContext from "views/components/Editor/EditorContext";
import { getAnimationStyles } from "lib/animationUtils";
import {
  getPageHtmlNode,
  clientToDesignPoint
} from "views/components/Editor/utils";
import { constrainDeltaToAxis } from "lib/geometry/snapping";
import { getElementsAtPoint } from "lib/geometry/hitTest";
import { isHeavyVector } from "views/components/Editor/elements/vector/heavyVector";

/**
 * Implements the drag source contract.
 */
const DraggableElementSource = {
  canDrag(props, monitor, component) {
    const { restrictions } = props.element;
    const { areFunctionalitiesLocked, isTable2MovementRestricted } = props;

    // Check standard restrictions first
    if (
      restrictions.includes("sizeAndPosition") ||
      restrictions.includes("position") ||
      areFunctionalitiesLocked ||
      isTable2MovementRestricted
    ) {
      return false;
    }

    // Check if element is currently invisible due to animation timing
    // This prevents invisible elements from blocking clicks to elements behind them
    if (component && component.isInvisibleDueToAnimation) {
      if (component.isInvisibleDueToAnimation()) {
        return false;
      }
    }

    // On mobile, only selected elements can be dragged.
    // Unselected elements must be tapped to select first.
    const ctx = component?.context;
    if (ctx?.isMobile && !props.isSelected) {
      return false;
    }

    return true;
  },

  beginDrag(props, monitor, component) {
    const { element, toggleDragging } = props;

    // Fix 1: Set drag flag so usePinchZoom skips pan processing
    const ctx = component?.context;
    if (ctx?.isDraggingElementRef) {
      ctx.isDraggingElementRef.current = true;
    }

    const snapCoordinatesSystem = SnapCoordinatesSystem.Builder({
      pageId: props.pageId,
      excludeIds: [element.uniqueId]
    });

    toggleDragging(true);
    const rotatedElementBounding = component.element.firstElementChild.getBoundingClientRect();

    const topOffset =
      (rotatedElementBounding.height - props.element.height * props.zoom) / 2;

    const leftOffset =
      (rotatedElementBounding.width - props.element.width * props.zoom) / 2;

    return {
      id: props.element.uniqueId,
      left: props.element.left,
      top: props.element.top,
      element: props.element,
      topOffset: topOffset,
      leftOffset: leftOffset,
      pageId: props.pageId,
      fromPageIndex: props.pageNumber,
      groupId: props.groupId,
      initScrollVertical: props.containerHtmlElement.scrollTop,
      initScrollHorizontal: props.containerHtmlElement.scrollLeft,
      snapSystem: snapCoordinatesSystem
    };
  },
  endDrag(props, monitor) {
    // Fix 1: Clear drag flag so usePinchZoom can resume pan processing
    if (window.easil?.editor?.isDraggingElementRef) {
      window.easil.editor.isDraggingElementRef.current = false;
    }

    /* handle when user press ESC while dragging*/
    if (!monitor.getDropResult()) {
      props.toggleDragging(false);
      return;
    }

    const { backgroundItemDrop } = monitor.getDropResult();

    let {
      delta,
      pageId: toPageId,
      pageNumber: toPageIndex,
      canvasHeight,
      verticalScrollDiff,
      horizontalScrollDiff
    } = monitor.getDropResult();

    const item = monitor.getItem();

    if (!isNil(backgroundItemDrop)) {
      const pageIndexOfDrop = backgroundItemDrop.getPageIndexOfDrop();
      if (!pageIndexOfDrop) {
        if (item.element.restrictions.includes("removable")) return;

        props.onDelete({
          id: item.id,
          pageId: item.pageId,
          groupId: item.groupId
        });
        return;
      } else {
        toPageId = window.easil.editor.designData.pagesOrder[pageIndexOfDrop];
        toPageIndex = pageIndexOfDrop + 1;
        canvasHeight = window.easil.editor.designData.height * props.zoom;
        delta = backgroundItemDrop.getMovementDelta();
        ({
          x: horizontalScrollDiff,
          y: verticalScrollDiff
        } = backgroundItemDrop.getScrollDiff());
      }
    }

    const left = item.left * props.zoom + delta.x;
    const top = item.top * props.zoom + delta.y;

    if (toPageId !== item.pageId) {
      const gutterZoomed = canvasMargin * 2 * props.zoom;
      const pageAndGutterZoomedSize = canvasHeight + gutterZoomed;

      const numberOfPagesMoved = Math.abs(item.fromPageIndex - toPageIndex);

      const pagesMovedOffset = pageAndGutterZoomedSize * numberOfPagesMoved;

      const directionFactor =
        item.top * props.zoom + verticalScrollDiff + delta.y < 0 ? 1 : -1;

      const newTop =
        top + verticalScrollDiff + pagesMovedOffset * directionFactor;

      return props.onMoveToPage({
        toPage: toPageId,
        id: item.id,
        left: left + horizontalScrollDiff,
        top: newTop,
        fromPage: item.pageId
      });
    }

    item.snapSystem.removeScrollEventListener();

    props.onMove({
      id: item.id,
      left: left + horizontalScrollDiff,
      top: top + verticalScrollDiff
    });

    // apply selection to element when drag has completed
    if (!props.isSelected) {
      props.onSelectItem({
        itemId: props.element.uniqueId,
        groupId: props.groupId,
        pageId: props.pageId
      });
    }
  }
};

/**
 * Specifies the props to inject into your component.
 */
function collect(connect, monitor) {
  return {
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    isDragging: monitor.isDragging()
  };
}

class DraggableElement extends Component {
  static contextType = EditorContext;

  constructor(props) {
    super(props);

    this.setElementRef = element => {
      this.element = element;
    };

    const { ElementComponent, componentProps } = getElementComponent(
      props.element.type
    );

    this.onSelect = this.onSelect.bind(this);
    this.handleCycleClick = this.handleCycleClick.bind(this);
    this.isInvisibleDueToAnimation = this.isInvisibleDueToAnimation.bind(this);
    this.state = {
      ElementComponent,
      componentProps
    };

    // For line element manual drag - store original position for direct DOM manipulation
    this.lineDragStartPosition = null;
    this.lineSnapSystem = null;
    this.lineSnapResult = null;
  }

  /**
   * Checks if an element is currently invisible due to animation timing.
   * This is used to prevent invisible elements from blocking interactions with elements behind them.
   * @returns {boolean} True if element is invisible due to animation state
   */
  isInvisibleDueToAnimation() {
    const context = this.context || {};
    const { isPlaying, isPaused, currentPlayingTime } = context;
    const { pageId, element, zoom } = this.props;

    const isPlayingForPage = isPlaying?.[pageId] || false;
    const isPausedForPage = isPaused?.[pageId] || false;
    const currentPlayingTimeForPage = currentPlayingTime?.[pageId] || 0;

    // Only check visibility when timeline is active (playing or paused)
    if (!isPlayingForPage && !isPausedForPage) {
      return false;
    }

    const animationStyles = getAnimationStyles(
      element,
      isPlayingForPage,
      pageId,
      zoom,
      currentPlayingTimeForPage,
      isPausedForPage
    );

    // Element is invisible if both opacity is 0 and visibility is hidden
    return (
      animationStyles &&
      animationStyles.opacity === 0 &&
      animationStyles.visibility === "hidden"
    );
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    const elementPropsThatMatters = [
      ...this.state.componentProps,
      "type",
      "left",
      "top",
      "scaleX",
      "scaleY",
      "angle",
      "value",
      "displayValue",
      "restrictions",
      "svgInstructions",
      "cssAnimation",
      "blendMode"
    ];
    const elementData = pick(this.props.element, elementPropsThatMatters);
    const nextElementData = pick(nextProps.element, elementPropsThatMatters);

    if (!jsonStringEqual(elementData, nextElementData)) {
      return true;
    }

    const propsThatMatter = [
      "isDragging",
      "zoom",
      "isSelected",
      "isMoveCursorHidden",
      "isHighlighted",
      "isAiBackgroundRemovalLoading"
    ];
    const currentPropsThatMatter = pick(this.props, propsThatMatter);
    const nextPropsThatMatter = pick(nextProps, propsThatMatter);

    if (!jsonStringEqual(currentPropsThatMatter, nextPropsThatMatter)) {
      return true;
    }

    // Check if timeline state changed for this page
    // This is needed to update canDrag() when animation visibility changes
    const currentContext = this.context || {};
    const nextCtx = nextContext || {};
    const pageId = this.props.pageId;

    const currentTimelineState = {
      isPlaying: currentContext.isPlaying?.[pageId],
      isPaused: currentContext.isPaused?.[pageId],
      currentPlayingTime: currentContext.currentPlayingTime?.[pageId]
    };
    const nextTimelineState = {
      isPlaying: nextCtx.isPlaying?.[pageId],
      isPaused: nextCtx.isPaused?.[pageId],
      currentPlayingTime: nextCtx.currentPlayingTime?.[pageId]
    };

    return !jsonStringEqual(currentTimelineState, nextTimelineState);
  }

  componentDidUpdate(prevProps) {
    if (this.props.element.type !== prevProps.element.type) {
      const { ElementComponent, componentProps } = getElementComponent(
        this.props.element.type
      );

      this.setState({
        ElementComponent,
        componentProps
      });
      this.forceUpdate();
    }
  }

  componentDidMount() {
    const { connectDragPreview } = this.props;
    if (connectDragPreview) {
      connectDragPreview(getEmptyImage(), {
        captureDraggingState: true
      });
    }
  }

  onSelect(args) {
    // Check if element is currently invisible due to animation timing
    // If so, don't allow selection - this allows clicks to pass through to elements behind
    if (this.isInvisibleDueToAnimation()) {
      return;
    }

    this.props.onSelectItem({
      itemId: this.props.element.uniqueId,
      groupId: this.props.groupId,
      pageId: this.props.pageId,
      ...args
    });
  }

  handleCycleClick(e) {
    // Ctrl (Windows) or Cmd (Mac) + click cycles through overlapping layers
    if (!(e.metaKey || e.ctrlKey) || e.shiftKey) return;

    const { element, pageId, designData } = this.props;
    const pageEl = getPageHtmlNode(pageId);
    if (!pageEl) return;

    const point = clientToDesignPoint(e.clientX, e.clientY, pageEl, designData);

    const overlapping = getElementsAtPoint({
      designData,
      pageId,
      point
    });

    if (overlapping.length <= 1) return;

    // Find current element in the overlapping list
    const currentIndex = overlapping.findIndex(
      item => item.element.uniqueId === element.uniqueId
    );

    // Select the next element below (wrap to top if at bottom)
    const nextIndex =
      currentIndex >= 0 ? (currentIndex + 1) % overlapping.length : 0;
    const next = overlapping[nextIndex];

    e.stopPropagation();
    e.preventDefault();

    this.props.onSelectItem({
      itemId: next.element.uniqueId,
      groupId: next.groupId,
      pageId
    });
  }

  isAllRestrictionsLocked() {
    const { designData, element: elementData } = this.props;
    if (!designData || !elementData) return false;

    const element = designData.getElement(elementData.uniqueId);

    const restrictions = element.getRestrictionsMap();

    // if any restrictions are not locked then this is false
    return !some(
      Object.values(restrictions),
      restriction => restriction === false
    );
  }

  // Manual drag handlers for line elements (react-dnd doesn't work with SVG)
  // We use direct DOM manipulation for real-time preview (more reliable than state for high-frequency updates)
  handleLineDragStart = () => {
    const { element, pageId } = this.props;
    const context = this.context || {};
    const selectedItems = context.selectedItems || [];

    // Fix 1: Set drag flag for line element manual drag
    if (context.isDraggingElementRef) {
      context.isDraggingElementRef.current = true;
    }

    // Store starting position for calculating final delta
    this.lineDragStartPosition = {
      left: element.left,
      top: element.top
    };

    // For multi-select: store starting positions and DOM refs for all other selected elements
    this.otherSelectedElements = [];
    if (selectedItems.length > 1) {
      const designData = this.props.designData;
      selectedItems.forEach(item => {
        if (item.itemId === element.uniqueId) return;
        const el = designData.getElement(item.itemId);
        if (!el) return;
        const domNode = document.getElementById(item.itemId);
        if (!domNode) return;
        this.otherSelectedElements.push({
          itemId: item.itemId,
          startLeft: el.left,
          startTop: el.top,
          domNode
        });
      });
    }

    // Initialize snap system for this drag
    this.lineSnapResult = null;

    // Check if snapping is enabled
    const excludeIds = [element.uniqueId, ...selectedItems.map(i => i.itemId)];
    if (getPath(window, "easil.editor.state.snapToCorners")) {
      this.lineSnapSystem = SnapCoordinatesSystem.Builder({
        pageId,
        excludeIds
      });
    } else {
      this.lineSnapSystem = null;
    }

    // Store snap guide state globally for rendering
    if (window.easil?.editor) {
      window.easil.editor.lineSnapGuides = { x: null, y: null };
    }

    // Hide the selection box during line drag (bypasses React render cycle)
    const selectionBox = document.getElementById("single-selection-box");
    if (selectionBox) {
      selectionBox.style.visibility = "hidden";
    }
    const multiSelectionBox = document.getElementById("multi-selection-box");
    if (multiSelectionBox) {
      multiSelectionBox.style.visibility = "hidden";
    }
  };

  handleLineDragMove = ({ deltaX, deltaY, shiftKey }) => {
    // Directly update DOM transform for real-time preview (bypasses React render cycle)
    if (this.element && this.lineDragStartPosition) {
      const { left, top } = this.lineDragStartPosition;
      const zoom = this.props.zoom;
      const { element, pageId } = this.props;

      // When Shift is held, constrain movement to horizontal or vertical axis
      let isShiftConstrained = false;
      let isHorizontal = false;
      if (shiftKey) {
        const constrained = constrainDeltaToAxis(deltaX, deltaY);
        isHorizontal = constrained.deltaY === 0;
        isShiftConstrained = true;
        deltaX = constrained.deltaX;
        deltaY = constrained.deltaY;
      }

      let snappedDeltaX = deltaX;
      let snappedDeltaY = deltaY;

      // Calculate snap points if snap system is available
      if (this.lineSnapSystem) {
        // Get current element position in design coordinates
        const newLeft = left + deltaX;
        const newTop = top + deltaY;

        // Get the page's bounding rect to convert to screen coordinates
        const pageNode = getPageHtmlNode(pageId);
        const pageBounds = pageNode?.getBoundingClientRect() || { x: 0, y: 0 };

        // Convert element position to screen coordinates for snap calculation
        const screenX = newLeft * zoom + pageBounds.x;
        const screenY = newTop * zoom + pageBounds.y;

        // Get element snap offsets (for bounding box snapping)
        // For lines, we use the bounding box left/top/center/right/bottom
        const width = element.width || 0;
        const height = element.height || 0;
        const elementSnapPointsOffset = {
          x: {
            left: 0,
            center: (width / 2) * zoom,
            right: width * zoom
          },
          y: {
            top: 0,
            center: (height / 2) * zoom,
            bottom: height * zoom
          }
        };

        // Calculate snap points
        const snapResult = SnapElement.getPoints({
          elementSnapPointsOffset,
          zoom,
          snapPoints: this.lineSnapSystem.getSnapPointsCorrected(),
          pointerPosition: { x: screenX, y: screenY }
        });

        this.lineSnapResult = snapResult;

        // Apply snap adjustments (when shift-constrained, only snap on the free axis)
        if (
          snapResult.x.elementCoordinateDiffFromPointerPosition &&
          (!isShiftConstrained || isHorizontal)
        ) {
          snappedDeltaX =
            deltaX +
            snapResult.x.elementCoordinateDiffFromPointerPosition / zoom;
        }
        if (
          snapResult.y.elementCoordinateDiffFromPointerPosition &&
          (!isShiftConstrained || !isHorizontal)
        ) {
          snappedDeltaY =
            deltaY +
            snapResult.y.elementCoordinateDiffFromPointerPosition / zoom;
        }

        // Update global snap guide state for rendering
        if (window.easil?.editor) {
          window.easil.editor.lineSnapGuides = {
            x: snapResult.x.coordinate,
            y: snapResult.y.coordinate
          };
        }
      }

      const newLeft = (left + snappedDeltaX) * zoom;
      const newTop = (top + snappedDeltaY) * zoom;
      this.element.style.transform = `translate3d(${newLeft}px, ${newTop}px, 0) scale(${zoom})`;

      // Move all other selected elements by the same delta
      if (this.otherSelectedElements) {
        this.otherSelectedElements.forEach(
          ({ startLeft, startTop, domNode }) => {
            const otherLeft = (startLeft + snappedDeltaX) * zoom;
            const otherTop = (startTop + snappedDeltaY) * zoom;
            domNode.style.transform = `translate3d(${otherLeft}px, ${otherTop}px, 0) scale(${zoom})`;
          }
        );
      }
    }
  };

  handleLineDragEnd = ({ deltaX, deltaY, shiftKey }) => {
    const { element } = this.props;
    const context = this.context || {};

    // Fix 1: Clear drag flag for line element manual drag
    if (context.isDraggingElementRef) {
      context.isDraggingElementRef.current = false;
    }

    // When Shift is held, constrain movement to horizontal or vertical axis
    let isShiftConstrained = false;
    let isHorizontal = false;
    if (shiftKey) {
      const constrained = constrainDeltaToAxis(deltaX, deltaY);
      isHorizontal = constrained.deltaY === 0;
      isShiftConstrained = true;
      deltaX = constrained.deltaX;
      deltaY = constrained.deltaY;
    }

    // Calculate new position from stored start position
    const startLeft = this.lineDragStartPosition?.left ?? element.left;
    const startTop = this.lineDragStartPosition?.top ?? element.top;

    let snappedDeltaX = deltaX;
    let snappedDeltaY = deltaY;

    // Apply snap adjustments (when shift-constrained, only snap on the free axis)
    if (this.lineSnapResult) {
      if (
        this.lineSnapResult.x.elementCoordinateDiffFromPointerPosition &&
        (!isShiftConstrained || isHorizontal)
      ) {
        snappedDeltaX +=
          this.lineSnapResult.x.elementCoordinateDiffFromPointerPosition /
          this.props.zoom;
      }
      if (
        this.lineSnapResult.y.elementCoordinateDiffFromPointerPosition &&
        (!isShiftConstrained || !isHorizontal)
      ) {
        snappedDeltaY +=
          this.lineSnapResult.y.elementCoordinateDiffFromPointerPosition /
          this.props.zoom;
      }
    }

    const newLeft = startLeft + snappedDeltaX;
    const newTop = startTop + snappedDeltaY;

    // Clean up snap system
    if (this.lineSnapSystem) {
      this.lineSnapSystem.removeScrollEventListener();
      this.lineSnapSystem = null;
    }

    // Clear drag state
    this.lineDragStartPosition = null;
    this.lineSnapResult = null;
    this.otherSelectedElements = null;

    // Clear global snap guides
    if (window.easil?.editor) {
      window.easil.editor.lineSnapGuides = null;
    }

    // Restore selection boxes after line drag
    const selectionBox = document.getElementById("single-selection-box");
    if (selectionBox) {
      selectionBox.style.visibility = "";
    }
    const multiSelectionBox = document.getElementById("multi-selection-box");
    if (multiSelectionBox) {
      multiSelectionBox.style.visibility = "";
    }

    // Check if multiple items are selected
    const selectedItems = context.selectedItems || [];
    const isMultiSelect = selectedItems.length > 1 && this.props.isSelected;

    if (isMultiSelect && context.moveSelection) {
      // Multi-element move: move all selected elements by the same delta
      const designData = this.props.designData;
      const selectedElements = selectedItems
        .map(item => designData.getElement(item.itemId))
        .filter(Boolean);

      context.moveSelection({
        selectedElements,
        left: snappedDeltaX * this.props.zoom,
        top: snappedDeltaY * this.props.zoom,
        toPageId: this.props.pageId,
        fromPageId: this.props.pageId,
        zoom: this.props.zoom
      });
    } else {
      // Single element move
      this.props.onMove({
        id: element.uniqueId,
        left: newLeft * this.props.zoom,
        top: newTop * this.props.zoom
      });
    }

    // Apply selection if not already selected
    if (!this.props.isSelected) {
      this.props.onSelectItem({
        itemId: element.uniqueId,
        groupId: this.props.groupId,
        pageId: this.props.pageId
      });
    }
  };

  render() {
    const { left, top, scaleX, scaleY, angle } = this.props.element;
    const {
      connectDragSource,
      isSelected,
      isMoveCursorHidden,
      isDesignPartOfACollection,
      isHighlighted,
      isDragging,
      isResizing
    } = this.props;
    const { ElementComponent, componentProps } = this.state;
    const context = this.context || {};

    // On mobile, don't connect react-dnd's drag source for unselected elements.
    // This prevents the TouchBackend from intercepting touches entirely,
    // allowing them to pass through to usePinchZoom for canvas panning.
    const effectiveDragSource =
      context.isMobile && !isSelected ? el => el : connectDragSource;

    // Empty style object since rotation and scaling are now on the wrapper
    // This prevents double transformation
    const style = {};

    const elementData = { ...pick(this.props.element, componentProps) };

    let opacity = "1";

    if (isDragging) {
      // Heavy vectors stay visible during *drag* — CustomDragLayer writes
      // style.transform directly to the canvas element so it can act as
      // its own preview without mounting a second 12k-node Vector tree.
      // For non-heavy elements, CardPreview renders the ghost, so hide the
      // real element.
      opacity = isHeavyVector(this.props.element) ? "1" : "0";
    } else if (isResizing && isSelected) {
      // During resize every element hides and the drag layer shows a
      // preview. Heavy vectors use the rasterised <img>-based
      // HeavyResizePreview — see scalingPreview/HeavyResizePreview.tsx.
      opacity = "0";
    }

    if (!isHighlighted) {
      // not highlighted elements should be semi-transparent
      opacity = "0.2";
    }

    if (PATHS.isRenderer(window.location.pathname)) {
      // always have full opacity when rendering
      opacity = "1";
    }

    // TEMPORARY FIX: Force caption elements to always be visible during development
    // TODO: Remove this once caption highlighting is properly integrated
    if (this.props.element.type === "caption") {
      opacity = "1";
    }

    // Apply rotation to wrapper to ensure bounding box rotates for proper click detection
    // This fixes the issue where rotated elements have axis-aligned bounding boxes
    // We rotate around the element's center by translating to center, rotating, then translating back
    // while keeping transformOrigin at 0,0 to preserve child element positioning
    const { width, height } = this.props.element;

    // Check if element should be non-interactive due to animation visibility
    // Only apply pointer-events: none to non-selected invisible elements
    // Selected elements should always remain interactive even if invisible
    // Don't apply this logic during drag/resize operations to avoid interfering with those interactions
    const shouldDisablePointerEvents =
      !isSelected &&
      !isDragging &&
      !isResizing &&
      this.isInvisibleDueToAnimation();

    const blendMode = this.props.element.blendMode;

    let styleWrapper = {
      // width: 0,
      // height: 0,
      cursor: isMoveCursorHidden ? "auto" : "move",
      position: "absolute",
      zIndex: "2",
      transform: `translate3d(${left * this.props.zoom}px, ${top *
        this.props.zoom}px, 0) scale(${this.props.zoom}) translate(${width /
        2}px, ${height /
        2}px) rotate(${angle}deg) scaleX(${scaleX}) scaleY(${scaleY}) translate(${-width /
        2}px, ${-height / 2}px)`,
      transformOrigin: "0 0",
      opacity,
      mixBlendMode: blendMode || "normal",
      visibility: this.props.hide ? "hidden" : "visible",
      pointerEvents: shouldDisablePointerEvents ? "none" : "revert"
    };

    // Line elements use their own coordinate system based on absolute pixel coordinates.
    // They don't need rotation/flip transforms since they're defined by endpoint positions.
    // They need translate3d for positioning and scale for zoom.
    // During manual drag, handleLineDragMove updates transform directly on the DOM.
    if (this.props.element.type === "line") {
      styleWrapper.transform = `translate3d(${left * this.props.zoom}px, ${top *
        this.props.zoom}px, 0) scale(${this.props.zoom})`;
    }

    const startImageCropMode = this.props.element.restrictions.includes(
      "cropping"
    )
      ? noop
      : this.props.startImageCropMode;

    const startVideoCropMode = this.props.element.restrictions.includes(
      "cropping"
    )
      ? noop
      : this.props.startVideoCropMode;

    const startVectorCropMode = this.props.element.restrictions.includes(
      "cropping"
    )
      ? noop
      : this.props.startVectorCropMode;

    const isAllRestrictionsLocked = this.isAllRestrictionsLocked();

    if (PATHS.isRenderer(window.location.pathname) && elementData.link) {
      // ensure link is valid and add protocol if it is missing
      const parsedLink = parseLinkUrl(elementData.link);
      return (
        <a
          ref={this.setElementRef}
          style={{
            ...styleWrapper,
            width: "auto",
            height: "auto",
            display: "block"
          }}
          id={this.props.element.uniqueId}
          href={parsedLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ElementComponent
            designData={this.props.designData}
            zoom={this.props.zoom}
            isPreview={this.props.isPreview}
            style={style}
            key={this.props.element.uniqueId}
            elementId={this.props.element.uniqueId}
            elementData={elementData}
            connectDragSource={effectiveDragSource}
            onSelectItem={this.onSelect}
            onElementAttributeChange={this.props.onElementAttributeChange}
            onElementPreview={this.props.onElementPreview}
            startImageCropMode={startImageCropMode}
            startVideoCropMode={startVideoCropMode}
            startVectorCropMode={startVectorCropMode}
            isSelected={isSelected}
            restrictions={this.props.element.restrictions}
            pageId={this.props.pageId}
            groupId={this.props.groupId}
            isDesignPartOfACollection={isDesignPartOfACollection}
            isAllRestrictionsLocked={isAllRestrictionsLocked}
          />
        </a>
      );
    }

    return (
      <div
        ref={this.setElementRef}
        style={styleWrapper}
        id={this.props.element.uniqueId}
        onClickCapture={this.handleCycleClick}
      >
        <ElementComponent
          designData={this.props.designData}
          zoom={this.props.zoom}
          isPreview={this.props.isPreview}
          style={style}
          key={this.props.element.uniqueId}
          elementId={this.props.element.uniqueId}
          elementData={elementData}
          connectDragSource={effectiveDragSource}
          onSelectItem={this.onSelect}
          onElementAttributeChange={this.props.onElementAttributeChange}
          onElementPreview={this.props.onElementPreview}
          startImageCropMode={startImageCropMode}
          startVideoCropMode={startVideoCropMode}
          startVectorCropMode={startVectorCropMode}
          isSelected={isSelected}
          restrictions={this.props.element.restrictions}
          pageId={this.props.pageId}
          groupId={this.props.groupId}
          isDesignPartOfACollection={isDesignPartOfACollection}
          isAllRestrictionsLocked={isAllRestrictionsLocked}
          isDragging={this.props.isDragging}
          isResizing={this.props.isResizing}
          isAiBackgroundRemovalLoading={this.props.isAiBackgroundRemovalLoading}
          // Manual drag handlers for line elements (react-dnd doesn't work with SVG)
          onDragStart={this.handleLineDragStart}
          onDragMove={this.handleLineDragMove}
          onDragEnd={this.handleLineDragEnd}
        />
      </div>
    );
  }
}

// Export the wrapped component:
export default DragSource(
  "ITEM",
  DraggableElementSource,
  collect
)(DraggableElement);
